import React, { useCallback, useMemo, useEffect, useState } from "react";
import {
  useParams,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useActiveTab,
  useFormData,
  useRequestDetails,
} from "../../../../hooks";
import Tab from "apollo-react/components/Tab";
import Tabs from "apollo-react/components/Tabs";
import Paper from "apollo-react/components/Paper";
import CircularProgress from "apollo-react/components/CircularProgress";
import StarSolid from "apollo-react-icons/StarSolid";
import Box from "apollo-react/components/Box";
import Button from "apollo-react/components/Button";
import { toast } from "react-toastify";

import FormSections from "../../../common/FormApproveComponents/FormSections/FormSections";
import Ectd from "../../../common/ECTD";
import DrugPricingForm from "./DrugPricingForm/DrugPricingForm";
import ClarificationWaves from "../../../common/ClarificationWaves";
import AdditionalDocuments from "../../../common/AdditionalDocuments/AdditionalDocuments";
import {
  MultiFormProvider,
  useMultiForm,
} from "../../../../contexts/MultiFormContext";
import AuditTrails from "../../../common/AuditTrails";
import { FORM_TYPES } from "../../../../constants/formConstants";
import { PaymentConfirmationModal } from "../../../Payment";

const RequestDetailsInner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const [isBackgroundRendering, setIsBackgroundRendering] = useState(false);
  const [comments, setComments] = useState([]);
  const [additionalDocsComments, setAdditionalDocsComments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const location = useLocation();
  const customRequestName = searchParams.get("request_name");
  const paymentFailed = searchParams.get("paymentFailed") === "true";

  const {
    setFormSectionsDataRef,
    syncFormSectionsData,
    syncAllTabsData,
    errors,
    formSections,
    subForms,
    filledForm,
    formName,
    activeSubFormTab,
    setActiveSubFormTab: originalSetActiveSubFormTab,
    requestId,
    requestProgressId,
    isSendbackMode,
    ectdUpload,
    submissionState,
    saveAsDraft,
    resetForms,
    submitAllForms,
    hasProductDetails,
    hasManufacturersSite,
    productDetails,
    validateForms,
    additionalDocuments,
    addAdditionalDocument,
    updateAdditionalDocument,
    deleteAdditionalDocument,
    drugPricing,
    manufacturersSite,
  } = useMultiForm();

  // Wrap setActiveSubFormTab to sync data before tab changes
  const setActiveSubFormTab = useCallback(
    async (newTabId) => {
      // sync jQuery form data before switching tabs
      try {
        await syncFormSectionsData();
      } catch (error) {
        console.error(
          "[details.js] Error syncing form data before tab change:",
          error
        );
      }

      originalSetActiveSubFormTab(newTabId);
    },
    [syncFormSectionsData, originalSetActiveSubFormTab]
  );

  const { requestProgress, parentStage, roleName } = useFormData(
    isSendbackMode ? requestProgressId : null,
    "customer"
  );

  const requestDetails =
    useRequestDetails(isSendbackMode ? requestId : null, "customer") || {};

  const TABS = useMemo(
    () => [
      { label: formName || "Main Form", disabled: false, value: 0 },
      { label: "Core Assessment", disabled: false, value: 1 },
      { label: "Pricing", disabled: false, value: 2 },
      { label: "Additional Documents", disabled: false, value: 3 },
      { label: t("clarificationWaves.tabLabel"), disabled: false, value: 4 },
      { label: "Audit Trail", disabled: false, value: 5 },
    ],
    [formName, t]
  );

  const [activeMainTab, setActiveMainTab] = useActiveTab(
    TABS,
    location.state?.openTab ?? 0
  );

  //different approach to validate on formSections because of jquery data
  useEffect(() => {
    if (activeMainTab !== 0) {
      // validate form sections, if user is not on main form
      validateForms([FORM_TYPES.FORM_SECTIONS]);
    }
  }, [activeMainTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "clarifications") {
      const clarificationsTabIndex = TABS.findIndex(
        (tab) => tab.label === t("clarificationWaves.tabLabel")
      );
      if (clarificationsTabIndex !== -1) {
        setActiveMainTab(clarificationsTabIndex);
      }
    }
  }, [searchParams, setActiveMainTab, TABS, t]);

  const populateJQueryFormsFromReactState = useCallback(async () => {
    if (
      subForms.length === 0 ||
      !formSections ||
      Object.keys(formSections).length === 0
    ) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 300));
  }, [formSections, subForms]);

  const handleTabChange = useCallback(
    async (event, value) => {
      if (activeMainTab === 0 && value !== 0) {
        try {
          await syncFormSectionsData();
          await syncAllTabsData(
            subForms,
            activeSubFormTab,
            setActiveSubFormTab
          );

          setIsBackgroundRendering(true);
          setTimeout(() => {
            // validate product details and manufacturers site before navigating to new tab
            validateForms([
              FORM_TYPES.PRODUCT_DETAILS,
              FORM_TYPES.MANUFACTURERS_SITE,
            ]);
            setActiveMainTab(value);
            setIsBackgroundRendering(false);
          }, 100);
          return;
        } catch (error) {
          // Continue with tab change even if sync fails
        }
      }

      setActiveMainTab(value);

      // validate current form before navigating to new tab
      if (activeMainTab === 1) {
        validateForms([FORM_TYPES.ECTD_UPLOAD]);
      } else if (activeMainTab === 2) {
        validateForms([FORM_TYPES.DRUG_PRICING]);
      }

      if (value === 0) {
        setIsBackgroundRendering(true);
        setTimeout(async () => {
          try {
            if (window.forceRenderAllForms) {
              await window.forceRenderAllForms();
              await syncFormSectionsData();
            }
          } catch (error) {
            // Silent error handling
          } finally {
            setIsBackgroundRendering(false);
          }
        }, 200);
      }
    },
    [
      activeMainTab,
      setActiveMainTab,
      syncFormSectionsData,
      syncAllTabsData,
      subForms,
      activeSubFormTab,
      setActiveSubFormTab,
      validateForms,
    ]
  );

  const dynamicFilledForm = useMemo(() => {
    if (!formSections || Object.keys(formSections).length === 0) {
      return filledForm;
    }

    const formDataForJQuery = {};
    subForms.forEach((sf) => {
      formDataForJQuery[sf.id] = formSections[sf.id] || {};
    });

    const dynamicForm = {
      ...filledForm,
      value: JSON.stringify(formDataForJQuery),
    };

    return dynamicForm;
  }, [formSections, subForms, filledForm]);

  useEffect(() => {
    const initialFormData = async () => {
      try {
        await syncFormSectionsData();
        await syncAllTabsData(subForms, activeSubFormTab, setActiveSubFormTab);

        setIsBackgroundRendering(true);
        setTimeout(() => {
          setIsBackgroundRendering(false);
        }, 100);
        return;
      } catch (error) {
        // Continue with tab change even if sync fails
      }
    };
    if (isSendbackMode) {
      if (activeMainTab === 0) {
        setTimeout(() => {
          initialFormData();
        }, 100);
      }
    }
  }, [isSendbackMode]);

  const forceRenderAllForms = useCallback(async () => {
    if (!subForms || subForms.length === 0) {
      return;
    }

    const originalTab = activeSubFormTab;
    const wasRendering = isBackgroundRendering;
    if (!wasRendering) {
      setIsBackgroundRendering(true);
    }

    try {
      for (const subForm of subForms) {
        setActiveSubFormTab(subForm.id);
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      if (originalTab) {
        setActiveSubFormTab(originalTab);
      } else if (subForms.length > 0) {
        setActiveSubFormTab(subForms[0].id);
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    } finally {
      if (!wasRendering) {
        setIsBackgroundRendering(false);
      }
    }
  }, [subForms, activeSubFormTab, setActiveSubFormTab, isBackgroundRendering]);

  useEffect(() => {
    window.populateJQueryFromReactState = populateJQueryFormsFromReactState;
    window.forceRenderAllForms = forceRenderAllForms;

    return () => {
      delete window.populateJQueryFromReactState;
      delete window.forceRenderAllForms;
    };
  }, [populateJQueryFormsFromReactState, forceRenderAllForms]);

  const handleSuccess = (validation) => {
    resetForms();
    window.history.go(-1);
  };

  // Show toast if redirected from failed payment and clear temp draft
  useEffect(() => {
    if (paymentFailed) {
      toast.error(
        t("payment.failedMessage") ||
          "Payment failed. Your request data has been restored. Please try again.",
        {
          position: "top-center",
          autoClose: 6000,
        }
      );

      // Clear the temp payment draft after a delay to ensure data is loaded
      // The draft data has been loaded by the MultiFormProvider via draftId param
      setTimeout(() => {
        const tempDraftKey = localStorage.getItem("payment_temp_draft_key");
        if (tempDraftKey) {
          localStorage.removeItem(tempDraftKey);
          localStorage.removeItem("payment_temp_draft_key");
          console.log("Cleared temp payment draft:", tempDraftKey);
        }
      }, 2000); // Give time for draft data to be fully loaded
    }
  }, [paymentFailed, t]);

  // Handle payment submission
  const handleSubmitWithPayment = useCallback(() => {
    // Show payment confirmation modal
    setShowPaymentModal(true);
  }, []);

  // Handle the actual form submission (called from payment modal)
  const handleConfirmPayment = useCallback(async () => {
    const result = await submitAllForms();
    if (result) {
      // submitAllForms now returns the response data which contains request_id
      // The response structure is: { request_id: ..., request_progress_id: ..., ... }
      const requestId = result?.request_id || result?.requestId || result?.id;

      console.log("Form submission result:", result);
      console.log("Extracted request_id:", requestId);

      return {
        success: true,
        requestId: requestId,
        request_id: requestId, // Include both formats for compatibility
        data: result,
      };
    }
    return { success: false, message: "Form submission failed" };
  }, [submitAllForms]);

  // Get draft data for recovery - includes all tabs and subtabs
  const getDraftDataForPayment = useCallback(() => {
    return {
      formSections,
      ectdUpload,
      productDetails,
      additionalDocuments,
      drugPricing,
      manufacturersSite,
      hasProductDetails,
      hasManufacturersSite,
      subForms,
    };
  }, [
    formSections,
    ectdUpload,
    productDetails,
    additionalDocuments,
    drugPricing,
    manufacturersSite,
    hasProductDetails,
    hasManufacturersSite,
    subForms,
  ]);

  const handleSaveAsDraft = useCallback(async () => {
    try {
      if (!customRequestName || !customRequestName.trim()) {
        toast.error(t("Please enter a draft name"), {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }

      const result = await saveAsDraft(customRequestName);

      if (!result.success) {
        toast.error(t("Failed to save draft"), {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }

      toast.success(t("draftSavedSuccessfully"), {
        position: "top-center",
        autoClose: 3000,
      });

      navigate("/customer/requests/my-requests?tab=4");
    } catch (error) {
      toast.error(t("Error saving draft"), {
        position: "top-center",
        autoClose: 3000,
      });
    }
  }, [saveAsDraft, customRequestName, t, navigate]);

  const getTabErrors = (tabIndex) => {
    const tabLabel = TABS[tabIndex]?.label;
    switch (tabLabel) {
      case formName:
      case "Main Form":
        return (
          errors.formSections?.hasErrors ||
          errors.productDetails?.hasErrors ||
          errors.manufacturersSite?.hasErrors ||
          false
        );
      case "Core Assessment":
        return errors.ectdUpload?.hasErrors || false;
      case "Pricing":
        return errors.drugPricing?.hasErrors || false;
      default:
        return false;
    }
  };

  return (
    <>
      <Tabs
        value={activeMainTab}
        onChange={handleTabChange}
        truncate
        className="form-approve-tabs"
        shape="box"
      >
        {TABS.map((tab, index) => (
          <Tab
            key={index}
            label={
              <>
                {activeMainTab === index && (
                  <StarSolid style={{ marginRight: "8px" }} />
                )}
                {tab.label}
                {getTabErrors(index) && (
                  <span
                    style={{
                      marginLeft: "8px",
                      color: "red",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    !
                  </span>
                )}
              </>
            }
            disabled={tab.disabled}
            className={`form-approve-tab ${
              getTabErrors(index) ? "tab-error" : ""
            }`}
            style={{
              borderBottom: getTabErrors(index) ? "2px solid red" : undefined,
            }}
          />
        ))}
      </Tabs>

      {TABS[activeMainTab]?.value === 0 && (
        <>
          {isBackgroundRendering ? (
            <Paper
              elevation={1}
              style={{
                padding: "40px",
                textAlign: "center",
                minHeight: "300px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress
                variant="indeterminate"
                statusText="Processing forms..."
              />
            </Paper>
          ) : (
            <FormSections
              subForms={subForms}
              requestProgressId={isSendbackMode ? requestProgressId : null}
              persona="customer"
              requestId={isSendbackMode ? requestId : null}
              filledForm={dynamicFilledForm}
              requestProgress={null}
              children={null}
              handleAction={null}
              isReadOnly={true}
              editable={true}
              onFormDataChange={setFormSectionsDataRef}
              syncFormSectionsData={syncFormSectionsData}
              skipApprovalFetch={true}
              // errors={errors.formSections}
              errors={{
                ...errors.formSections,
                productDetails: errors.productDetails,
                manufacturersSite: errors.manufacturersSite,
              }}
              activeTab={activeSubFormTab}
              onTabChange={setActiveSubFormTab}
              showRequestBadges={false}
              enableAddComment={isSendbackMode}
              hasProductDetails={hasProductDetails}
              hasManufacturersSite={hasManufacturersSite}
              isSendbackMode={isSendbackMode}
              productDetailsData={productDetails}
            />
          )}
        </>
      )}
      {TABS[activeMainTab]?.value === 1 && (
        <Ectd
          isEditable={true}
          initialUploadedFile={ectdUpload?.ectd_file[0]}
          role="customer"
          requestId={isSendbackMode ? requestId : null}
          isSendbackMode={isSendbackMode}
          ectdTreeComponentProps={{
            role: "customer",
            showDeleteButton: true,
            requestProgressStatus: isSendbackMode ? "sendback" : "",
            ectdVersion: requestDetails?.ectd_version,
          }}
        />
      )}
      {TABS[activeMainTab]?.value === 2 && (
        <DrugPricingForm isSendbackMode={isSendbackMode} />
      )}
      {TABS[activeMainTab]?.value === 3 && (
        <AdditionalDocuments
          requestId={isSendbackMode ? requestId : null}
          certificateFileId={null}
          role="customer"
          persona="customer"
          requestProgressStatus={requestProgress?.status}
          comments={additionalDocsComments}
          setComments={setAdditionalDocsComments}
          tempComments={additionalDocuments}
          onAddTempComment={addAdditionalDocument}
          onEditTempComment={updateAdditionalDocument}
          onDeleteTempComment={deleteAdditionalDocument}
          isNewRequest={!isSendbackMode}
        />
      )}
      {TABS[activeMainTab]?.value === 4 && (
        <ClarificationWaves
          subForms={subForms}
          requestProgressId={requestProgressId}
          persona="customer"
          requestId={requestId}
          filledForm={filledForm}
          requestProgress={requestProgress}
          requestDetails={{}}
          roleName={roleName}
          parentStage={parentStage}
          comments={comments}
          setComments={setComments}
        />
      )}
      {TABS[activeMainTab]?.value === 5 && (
        <AuditTrails
          persona="customer"
          requestID={isSendbackMode ? requestId : null}
          role="customer"
        />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          marginTop: 3,
          backgroundColor: "#f8f9fa",
        }}
      >
        {/* previous button: show on tabs 1, 2, 3 */}
        {(activeMainTab === 1 ||
          activeMainTab === 2 ||
          activeMainTab === 3) && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={(e) => handleTabChange(e, activeMainTab - 1)}
            sx={{ minWidth: 160 }}
          >
            Previous
          </Button>
        )}

        {/* save as draft: only on additional documents tab (3) */}
        {activeMainTab === 3 && !isSendbackMode && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSaveAsDraft}
            disabled={submissionState.isSubmitting}
            sx={{ minWidth: 160 }}
          >
            Save as Draft
          </Button>
        )}

        {/* submit button: only on additional documents tab (3) */}
        {activeMainTab === 3 && !isSendbackMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitWithPayment}
            disabled={submissionState.isSubmitting}
            sx={{ minWidth: 200 }}
          >
            {submissionState.isSubmitting ? "Processing..." : "Submit & Pay"}
          </Button>
        )}

        {/* submit button for sendback mode (no payment) */}
        {activeMainTab === 3 && isSendbackMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              const validation = await submitAllForms();
              if (validation) {
                handleSuccess(validation);
              }
            }}
            disabled={submissionState.isSubmitting}
            sx={{ minWidth: 200 }}
          >
            {submissionState.isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        )}

        {/* next button: show on tabs 0, 1, 2 */}
        {(activeMainTab === 0 ||
          activeMainTab === 1 ||
          activeMainTab === 2) && (
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => handleTabChange(e, activeMainTab + 1)}
            sx={{ minWidth: 160 }}
          >
            Next
          </Button>
        )}
      </Box>

      {/* Payment Confirmation Modal */}
      <PaymentConfirmationModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handleConfirmPayment}
        workflowVersionId={id}
        requestName={customRequestName}
        draftData={getDraftDataForPayment()}
        isSubmitting={submissionState.isSubmitting}
        submissionState={submissionState}
      />
    </>
  );
};

const RequestDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isSendbackMode = location.pathname.includes("/send-back/");
  const draftId = searchParams.get("draftId");
  const customRequestName = searchParams.get("request_name");

  const { requestId, loading: requestDataLoading } = useFormData(
    isSendbackMode ? id : null,
    "customer"
  );

  if (isSendbackMode && requestDataLoading) {
    return (
      <Paper
        elevation={1}
        style={{
          padding: "40px",
          textAlign: "center",
          minHeight: "300px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress
          variant="indeterminate"
          statusText="Loading request data..."
        />
      </Paper>
    );
  }

  return (
    <MultiFormProvider
      requestId={isSendbackMode ? requestId : null}
      stageId={null}
      workflowVersionId={isSendbackMode ? null : id}
      requestProgressId={isSendbackMode ? id : null}
      isSendbackMode={isSendbackMode}
      studyName={customRequestName || undefined}
      draftId={draftId || undefined}
    >
      <RequestDetailsInner />
    </MultiFormProvider>
  );
};

export default RequestDetails;
